import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Tabs, Tab, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';

const DBPanelForm = ({ role = cookie.get('user-role') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentSignature, setCurrentSignature] = useState({ level: '', role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      station_id: stationId || '',
      project_name: '',
      manufacturer: '',
      rated_output: '',
      serial_number: '',
      equipment_tag: '',

      // Level 1
      visual_checks: Array(13).fill({ status: '', remark: '' }),
      physical_checks: Array(7).fill({ status: '', remark: '' }),
      l1_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l1_status: 'draft',

      // Level 2
      db_healthy: '',
      functional_checks: '',
      testing_commissioning: '',
      ir_test: '',
      functional: Array(5).fill({ status: '', remark: '' }),
      instruments: [
        { serial: '', range: '', calibration: '' },
        { serial: '', range: '', calibration: '' },
      ],
      ir_tests: Array(10).fill({ value: '', remark: '' }),
      observations: { a: '', b: '', c: '' },
      l2_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l2_status: 'draft',

      // parity with APFC (used when manager moves to QUALITY)
      manager_approve: false,
    },
  });

  const formValues = useWatch({ control });
  const l1_status = useWatch({ control, name: 'l1_status' });
  const l2_status = useWatch({ control, name: 'l2_status' });

  // ---------- Autosave with computed common status (like APFC) ----------
  const autoSave = React.useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const l1_status = data.l1_status;
      const l2_status = data.l2_status;

      let common_status = 'draft';
      if (l1_status === 'CONTRACTOR' && l2_status === 'CONTRACTOR') common_status = 'CONTRACTOR';
      else if (l1_status === 'MANAGER' && l2_status === 'MANAGER') common_status = 'MANAGER';
      else if (l1_status === 'QUALITY' && l2_status === 'QUALITY') common_status = 'QUALITY';
      else if (l1_status === 'approved' && l2_status === 'approved') common_status = 'approved';

      const payload = {
        form_data: {
          status: common_status,
          form_id: formId,
          formName: 'db_panel',
          station_id: data.station_id,
          project_name: data.project_name,
          manufacturer: data.manufacturer,
          rated_output: data.rated_output,
          serial_number: data.serial_number,
          equipment_tag: data.equipment_tag,
          manager_approve: data.manager_approve,
          l1: {
            visual_checks: data.visual_checks,
            physical_checks: data.physical_checks,
            comments: data.l1_comments,
            status: l1_status,
          },
          l2: {
            db_healthy: data.db_healthy,
            functional_checks: data.functional_checks,
            testing_commissioning: data.testing_commissioning,
            ir_test: data.ir_test,
            functional: data.functional,
            instruments: data.instruments,
            ir_tests: data.ir_tests,
            observations: data.observations,
            comments: data.l2_comments,
            status: l2_status,
          },
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => { autoSave(formValues); }, [formValues, autoSave]);

  // ---------- Fetch existing ----------
  useEffect(() => {
    if (formId) {
      dispatch({ type: VIEW_FORM, payload: { formId, formName: 'db_panel' } });
    }
    setIsLoading(false);
  }, [dispatch, formId]);

  // ---------- Populate from store ----------
  useEffect(() => {
    if (viewFormData) {
      const d = viewFormData || {};
      setValue('station_id', d.station_id || stationId || '');
      setValue('project_name', d.project_name || '');
      setValue('manufacturer', d.manufacturer || '');
      setValue('rated_output', d.rated_output || '');
      setValue('serial_number', d.serial_number || '');
      setValue('equipment_tag', d.equipment_tag || '');

      setValue('visual_checks', Array.isArray(d.l1?.visual_checks) ? d.l1.visual_checks : Array(13).fill({ status: '', remark: '' }));
      setValue('physical_checks', Array.isArray(d.l1?.physical_checks) ? d.l1.physical_checks : Array(7).fill({ status: '', remark: '' }));
      setValue('l1_comments', d.l1?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l1_status', d.l1?.status || 'draft');

      setValue('db_healthy', d.l2?.db_healthy || '');
      setValue('functional_checks', d.l2?.functional_checks || '');
      setValue('testing_commissioning', d.l2?.testing_commissioning || '');
      setValue('ir_test', d.l2?.ir_test || '');
      setValue('functional', Array.isArray(d.l2?.functional) ? d.l2.functional : Array(5).fill({ status: '', remark: '' }));
      setValue('instruments', Array.isArray(d.l2?.instruments) ? d.l2.instruments : [
        { serial: '', range: '', calibration: '' },
        { serial: '', range: '', calibration: '' },
      ]);
      setValue('ir_tests', Array.isArray(d.l2?.ir_tests) ? d.l2.ir_tests : Array(10).fill({ value: '', remark: '' }));
      setValue('observations', d.l2?.observations || { a: '', b: '', c: '' });
      setValue('l2_comments', d.l2?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l2_status', d.l2?.status || 'draft');

      setValue('manager_approve', d.manager_approve || false);
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue, role]);

  // ---------- Signature helpers ----------
  const handleImageUpload = (e, level, sigRole) => {
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB
    if (file) {
      if (file.size > maxSizeInBytes) {
        alert('File size exceeds 50kb. Please upload a smaller image.');
        handleResetSignature(level, sigRole);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setValue(`${level}_comments.${sigRole}.signature`, reader.result, { shouldDirty: true, shouldValidate: true });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (level, sigRole, type) => {
    setValue(`${level}_comments.${sigRole}.signatureType`, type, { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    if (type === 'digital') {
      setCurrentSignature({ level, role: sigRole });
      setShowModal(true);
    }
  };

  const handleResetSignature = (level, sigRole) => {
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signatureType`, 'upload', { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`${currentSignature.level}_comments.${currentSignature.role}.signature`, signatureData, { shouldDirty: true, shouldValidate: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => { sigCanvas.current?.clear(); };
  const handleCloseModal = () => { setShowModal(false); sigCanvas.current?.clear(); };

  // ---------- Submit handlers (APFC-like) ----------
  const onSubmit = (level, status) => (data) => {
    // Early guards: only allow engineer/contractor at their stage
    if (role === 'engineer' && getValues(`${level}_status`) !== 'draft') return;
    if (role === 'contractor' && getValues(`${level}_status`) !== 'CONTRACTOR') return;

    let l1_status = data.l1_status;
    let l2_status = data.l2_status;
    let manager_approve = data.manager_approve;

    if (level === 'l1') l1_status = status;
    if (level === 'l2') l2_status = status;

    // Role-driven transitions
    if (role === 'engineer') {
      status = 'CONTRACTOR';
      if (level === 'l1') l1_status = 'CONTRACTOR';
      if (level === 'l2') l2_status = 'CONTRACTOR';
    } else if (role === 'contractor') {
      status = 'MANAGER';
      if (level === 'l1') l1_status = 'MANAGER';
      if (level === 'l2') l2_status = 'MANAGER';
    } else if (role === 'manager' && status === 'approved') {
      status = 'QUALITY';
      if (level === 'l1') l1_status = 'QUALITY';
      if (level === 'l2') l2_status = 'QUALITY';
      manager_approve = true;
    } else if (role === 'manager' && status === 'draft') {
      if (level === 'l1') l1_status = 'draft';
      if (level === 'l2') l2_status = 'draft';
    } else if (role === 'quality' && status === 'approved') {
      if (level === 'l1') l1_status = 'approved';
      if (level === 'l2') l2_status = 'approved';
    } else if (role === 'quality' && status === 'draft') {
      if (level === 'l1') l1_status = 'draft';
      if (level === 'l2') l2_status = 'draft';
    }

    // Common status
    let common_status = 'draft';
    if (l1_status === 'CONTRACTOR' && l2_status === 'CONTRACTOR') common_status = 'CONTRACTOR';
    else if (l1_status === 'MANAGER' && l2_status === 'MANAGER') common_status = 'MANAGER';
    else if (l1_status === 'QUALITY' && l2_status === 'QUALITY') common_status = 'QUALITY';
    else if (l1_status === 'approved' && l2_status === 'approved') common_status = 'approved';

    const payload = {
      form_data: {
        status: common_status,
        form_id: formId,
        formName: 'db_panel',
        station_id: data.station_id,
        project_name: data.project_name,
        manufacturer: data.manufacturer,
        rated_output: data.rated_output,
        serial_number: data.serial_number,
        equipment_tag: data.equipment_tag,
        manager_approve,

        l1: {
          visual_checks: data.visual_checks,
          physical_checks: data.physical_checks,
          comments: data.l1_comments,
          status: l1_status,
        },
        l2: {
          db_healthy: data.db_healthy,
          functional_checks: data.functional_checks,
          testing_commissioning: data.testing_commissioning,
          ir_test: data.ir_test,
          functional: data.functional,
          instruments: data.instruments,
          ir_tests: data.ir_tests,
          observations: data.observations,
          comments: data.l2_comments,
          status: l2_status,
        },
      },
    };

    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    toast.success(
      `Level ${level.toUpperCase()} ${
        status === 'pending' || status === 'approved' ? 'Submitted' : 'Saved as Draft'
      } Successfully`
    );
    navigate(-1);
  };

  // ---------- UI gating ----------
  const disableLevel1Tab = role === 'contractor' && getValues('l1_status') !== 'CONTRACTOR';
  const disableLevel2Tab = role === 'contractor' && getValues('l2_status') !== 'CONTRACTOR';

  const isLevel1Editable = (role === 'engineer' && l1_status === 'draft') || (role === 'contractor' && l1_status === 'CONTRACTOR');
  const isLevel2Editable = (role === 'engineer' && l2_status === 'draft') || (role === 'contractor' && l2_status === 'CONTRACTOR');

  const isSigEditable = (level, signatureRole) =>
    (role === 'engineer' && signatureRole === 'charge_engineer' && getValues(`${level}_status`) === 'draft') ||
    (role === 'contractor' && signatureRole === 'contractor' && getValues(`${level}_status`) === 'CONTRACTOR') ||
    (role === 'quality' && signatureRole === 'quality' && getValues(`${level}_status`) === 'QUALITY');

  if (isLoading) return <div>Loading...</div>;

  const renderSignatureBlock = (level, signatureRole, field) => {
    const editable = isSigEditable(level, signatureRole);

    if (field === 'Date') {
      return <Form.Control type="date" {...register(`${level}_comments.${signatureRole}.date`)} disabled={!editable} />;
    } else if (field === 'Signature') {
      return (
        <div>
          <Form.Select
            {...register(`${level}_comments.${signatureRole}.signatureType`)}
            onChange={(e) => handleSignatureTypeChange(level, signatureRole, e.target.value)}
            className="mb-2"
            disabled={!editable}
          >
            <option value="upload">Image Upload</option>
            <option value="digital">Digital Sign</option>
          </Form.Select>

          {_.get(formValues, `${level}_comments.${signatureRole}.signatureType`) === 'upload' && (
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, level, signatureRole)}
              className="mb-2"
              disabled={!editable}
            />
          )}

          {_.get(formValues, `${level}_comments.${signatureRole}.signature`) && (
            <div>
              <img
                src={_.get(formValues, `${level}_comments.${signatureRole}.signature`)}
                alt="Signature"
                style={{ maxWidth: '100px', marginTop: '5px' }}
              />
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleResetSignature(level, signatureRole)}
                className="mt-2"
                disabled={!editable}
              >
                Reset Signature
              </Button>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <Form.Control
          type="text"
          {...register(`${level}_comments.${signatureRole}.${field.toLowerCase()}`)}
          disabled={!editable}
        />
      );
    }
  };

  return (
    <div className="page-body container-xl">
      <h2 className="page-title mb-4">DB Panel - Site Acceptance Testing</h2>

      {(!isLevel1Editable && !isLevel2Editable && role === 'engineer') && (
        <div className="alert alert-warning mb-4">This form is in View Mode Only for Engineer.</div>
      )}

      <Form>
        <h3>1. Equipment Details</h3>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control type="text" {...register('project_name')} disabled={!(isLevel1Editable || isLevel2Editable)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Equipment ID</Form.Label>
              <Form.Control type="text" {...register('manufacturer')} disabled={!(isLevel1Editable || isLevel2Editable)} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Rated Output</Form.Label>
              <Form.Control type="text" {...register('rated_output')} disabled={!(isLevel1Editable || isLevel2Editable)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Serial Number</Form.Label>
              <Form.Control type="text" {...register('serial_number')} disabled={!(isLevel1Editable || isLevel2Editable)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Equipment Tag</Form.Label>
              <Form.Control type="text" {...register('equipment_tag')} disabled={!(isLevel1Editable || isLevel2Editable)} />
            </Form.Group>
          </Col>
        </Row>

        <Tabs defaultActiveKey="level1" id="sat-tabs" className="mb-3">
          <Tab eventKey="level1" title="Level 1 - Pre-Commissioning" disabled={disableLevel1Tab}>
            <h3 className="mt-4">2. Visual Checks</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
              </thead>
              <tbody>
                {[
                  'Is the identification of equipment tags as per the SLD?',
                  'Is the identification of the manufacturer’s nameplate as per the GTP?',
                  'Has the DB panel vendor TC check been completed?',
                  'Are gaskets present on the panel doors?',
                  'Are the lamps and indicators glowing during the visual check?',
                  'Is the panel properly double-earthed?',
                  'Are there any exposed live parts, and are they covered?',
                  'Is ventilation provided through louvers?',
                  'Is there any physical damage, and are the door locking arrangements intact?',
                  'Is the power cable termination tight, with proper markings?',
                  'Is the internal assembly free from damage and missing parts?',
                  'If any missing parts as per the SLD/GTP?',
                  'Is the location and orientation as per the design and drawing?',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`visual_checks_${index}`} value="Yes" {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="No"  type="radio" name={`visual_checks_${index}`} value="No"  {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="NA"  type="radio" name={`visual_checks_${index}`} value="NA"  {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                    </td>
                    <td><Form.Control type="text" {...register(`visual_checks.${index}.remark`)} disabled={!isLevel1Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">3. Physical Checks</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
              </thead>
              <tbody>
                {[
                  'Is there any loose control wiring, and is the cable dressing proper?',
                  'Are the internal components installed properly?',
                  'Are all cable glands tightened securely?',
                  'Is the panel board properly tightened on the base frame?',
                  'Is the earth strip/cable properly tightened with the panel?',
                  'Are the correct methods used for cable termination and tagging?',
                  'Is the panel functioning correctly in both automatic and manual operation modes?',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`physical_checks_${index}`} value="Yes" {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="No"  type="radio" name={`physical_checks_${index}`} value="No"  {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="NA"  type="radio" name={`physical_checks_${index}`} value="NA"  {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                    </td>
                    <td><Form.Control type="text" {...register(`physical_checks.${index}.remark`)} disabled={!isLevel1Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">4. Level 1 Pre-Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr><th></th><th>Engineer - Contractor</th><th>Engineer - Charge Zone</th><th>Quality - Charge Zone</th><th>Optional</th></tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((sigRole) => (
                      <td key={sigRole}>{renderSignatureBlock('l1', sigRole, field)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Actions for L1 */}
            {role === 'manager' && getValues('l1_status') === 'MANAGER' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'approved'))} className="me-2">Approve Level 1</Button>
                <Button variant="danger"  onClick={handleSubmit(onSubmit('l1', 'draft'))}>Re-Work Level 1</Button>
              </div>
            ) : role === 'quality' && getValues('l1_status') === 'QUALITY' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'approved'))} className="me-2">Approve Level 1</Button>
                <Button variant="danger"  onClick={handleSubmit(onSubmit('l1', 'draft'))}>Apply for Resubmit Level 1</Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  variant="success"
                  onClick={handleSubmit(onSubmit('l1', 'pending'))}
                  disabled={role === 'engineer' ? getValues('l1_status') !== 'draft' : role === 'contractor' ? getValues('l1_status') !== 'CONTRACTOR' : true}
                >
                  Submit Level 1
                </Button>
                <Button
                  variant="secondary"
                  style={{ marginLeft: '10px' }}
                  onClick={handleSubmit(onSubmit('l1', 'draft'))}
                  disabled={role === 'engineer' ? getValues('l1_status') !== 'draft' : role === 'contractor' ? getValues('l1_status') !== 'CONTRACTOR' : true}
                >
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>

          <Tab eventKey="level2" title="Level 2 - Commissioning" disabled={disableLevel2Tab}>
            <h3 className="mt-4">5. Commissioning Part</h3>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>DB Panel found healthy & ready to power ON</Form.Label>
                  <Form.Select {...register('db_healthy')} disabled={!isLevel2Editable}>
                    <option value="">Select</option><option value="Yes">Yes</option><option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Functional Checks</Form.Label>
                  <Form.Select {...register('functional_checks')} disabled={!isLevel2Editable}>
                    <option value="">Select</option><option value="Yes">Yes</option><option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Testing & Commissioning</Form.Label>
                  <Form.Select {...register('testing_commissioning')} disabled={!isLevel2Editable}>
                    <option value="">Select</option><option value="Yes">Yes</option><option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Insulation Resistance Test</Form.Label>
                  <Form.Select {...register('ir_test')} disabled={!isLevel2Editable}>
                    <option value="">Select</option><option value="Yes">Yes</option><option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h3 className="mt-4">6. Functional Checks</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
              </thead>
              <tbody>
                {[
                  'Protection Check - Automatic & Manual',
                  'Trial Tripping - MCCB, ELR, Contactor Tripping Test',
                  'Annunciation - If any',
                  'Alarms Trials - If any',
                  'All pilot lamps glow check',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`functional_${index}`} value="Yes" {...register(`functional.${index}.status`)} disabled={!isLevel2Editable} />
                      <Form.Check inline label="No"  type="radio" name={`functional_${index}`} value="No"  {...register(`functional.${index}.status`)} disabled={!isLevel2Editable} />
                      <Form.Check inline label="NA"  type="radio" name={`functional_${index}`} value="NA"  {...register(`functional.${index}.status`)} disabled={!isLevel2Editable} />
                    </td>
                    <td><Form.Control type="text" {...register(`functional.${index}.remark`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">7. Test Instruments Details and Calibration Status</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Instrument Description</th><th>Instrument Serial No.</th><th>Range</th><th>Calibration Status</th></tr>
              </thead>
              <tbody>
                {['IR Tester', 'Multi meter'].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td><Form.Control type="text" {...register(`instruments.${index}.serial`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`instruments.${index}.range`)}  disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`instruments.${index}.calibration`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">8. Insulation Resistance Test (Minimum value - 2 MΩ)</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Phase to Phase</th><th>Value (MΩ/GΩ)</th><th>Remarks</th></tr>
              </thead>
              <tbody>
                {['R-Y', 'Y-B', 'B-R', 'R-N', 'Y-N', 'B-N', 'R-E', 'Y-E', 'B-E', 'N-E'].map((phase, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{phase}</td>
                    <td><Form.Control type="text" {...register(`ir_tests.${index}.value`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`ir_tests.${index}.remark`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p>Note: During Megger testing isolate neutral link, remove Capacitor Bank, space heater fuses if any.</p>

            <h3 className="mt-4">9. Observations during Commissioning / Testing</h3>
            {['a', 'b', 'c'].map((key) => (
              <Form.Group className="mb-3" key={key}>
                <Form.Label>{key.toUpperCase()}.</Form.Label>
                <Form.Control type="text" {...register(`observations.${key}`)} disabled={!isLevel2Editable} />
              </Form.Group>
            ))}

            <h3 className="mt-4">10. Level 2 Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr><th></th><th>Engineer - Contractor</th><th>Engineer - Charge Zone</th><th>Quality - Charge Zone</th><th>Optional</th></tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((sigRole) => (
                      <td key={sigRole}>{renderSignatureBlock('l2', sigRole, field)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Actions for L2 */}
            {role === 'manager' && getValues('l2_status') === 'MANAGER' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'approved'))} className="me-2">Approve Level 2</Button>
                <Button variant="danger"  onClick={handleSubmit(onSubmit('l2', 'draft'))}>Re-Work Level 2</Button>
              </div>
            ) : role === 'quality' && getValues('l2_status') === 'QUALITY' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'approved'))} className="me-2">Approve Level 2</Button>
                <Button variant="danger"  onClick={handleSubmit(onSubmit('l2', 'draft'))}>Apply for Resubmit Level 2</Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  variant="success"
                  onClick={handleSubmit(onSubmit('l2', 'pending'))}
                  disabled={role === 'engineer' ? getValues('l2_status') !== 'draft' : role === 'contractor' ? getValues('l2_status') !== 'CONTRACTOR' : true}
                >
                  Submit Level 2
                </Button>
                <Button
                  variant="secondary"
                  style={{ marginLeft: '10px' }}
                  onClick={handleSubmit(onSubmit('l2', 'draft'))}
                  disabled={role === 'engineer' ? getValues('l2_status') !== 'draft' : role === 'contractor' ? getValues('l2_status') !== 'CONTRACTOR' : true}
                >
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>
        </Tabs>

        {/* Signature Modal */}
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton><Modal.Title>Draw Signature</Modal.Title></Modal.Header>
          <Modal.Body>
            <SignatureCanvas ref={sigCanvas} penColor="black" canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }} />
            <div className="mt-2">
              <Button variant="secondary" onClick={handleClearSignature} className="me-2">Clear</Button>
              <Button variant="primary" onClick={handleSaveSignature}>Save Signature</Button>
            </div>
          </Modal.Body>
        </Modal>
      </Form>
    </div>
  );
};

DBPanelForm.propTypes = {
  role: PropTypes.oneOf(['engineer', 'contractor', 'manager', 'quality', 'admin']),
};

export default DBPanelForm;
